import React, { useState } from 'react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { 
  Download, 
  ExternalLink, 
  CheckCircle2, 
  AlertCircle,
  FileText,
  Building2,
  ChevronDown,
  ChevronUp,
  Info,
  Users,
  ClipboardList
} from 'lucide-react';

const BetrieblicheErsteHilfePage = () => {
  const [openAccordion, setOpenAccordion] = useState(null);

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  // BGs that use the standard form
  const standardBGs = [
    'BG BAU',
    'BGETEM',
    'BGHM',
    'BG RCI',
    'BG Verkehr',
    'BGHW',
    'VBG'
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <SEO page="betrieblich" />
      <Navbar />
      
      {/* MOBILE: Klassischer Hero wie Dortmund (Bild oben, Titel darunter) */}
      <div className="md:hidden">
        {/* Vollbreites Bild */}
        <div className="w-full">
          <img 
            src="https://customer-assets.emergentagent.com/job_erste-hilfe-app-1/artifacts/weymkkl2_63C43804-D221-4282-AF4E-67D2D6F765F0.png"
            alt="Betriebliche Erste-Hilfe-Kurse"
            className="w-full h-auto object-cover"
            style={{ aspectRatio: '16/9' }}
          />
        </div>
        
        {/* Titel-Bereich unter dem Bild */}
        <div className="bg-white px-4 py-6">
          <h1 className="text-2xl font-bold text-[#1e3a5f] leading-tight mb-3" data-testid="page-title-mobile">
            Betriebliche Erste-Hilfe-Kurse<br />
            <span className="text-xl">gemäß DGUV / Berufsgenossenschaften</span>
          </h1>
          <p className="text-slate-600 text-base leading-relaxed">
            Ersthelferinnen und Ersthelfer für Ihren Betrieb – praxisnah, verständlich und bundesweit anerkannt.
          </p>
        </div>
      </div>

      {/* DESKTOP: Bestehender Hero mit Gradient-Overlay (unverändert) */}
      <div className="hidden md:block">
        <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div 
            className="relative overflow-hidden rounded-xl"
            style={{ 
              backgroundImage: `url("https://customer-assets.emergentagent.com/job_erste-hilfe-app-1/artifacts/weymkkl2_63C43804-D221-4282-AF4E-67D2D6F765F0.png")`,
              backgroundSize: '100% 100%',
              backgroundPosition: 'center center'
            }}
          >
            {/* Gradient Overlay - NUR links für Textbereich */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 via-[#1e3a5f]/70 to-[#1e3a5f]/20" />
            
            {/* Content - Einheitliche Höhe */}
            <div className="relative px-8 lg:px-10 py-12 lg:py-14">
              <div className="flex flex-row items-center justify-between gap-6 min-h-[240px]">
                {/* Titel + Zusatzinfo - Links */}
                <div className="max-w-xl">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white rounded-full text-sm font-semibold shadow-sm">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      BG/DGUV zertifiziert
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 text-[#1e3a5f] rounded-full text-sm font-semibold shadow-sm">
                      Kostenlos für Betriebe
                    </span>
                  </div>
                  
                  <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight drop-shadow-sm" data-testid="page-title">
                    Erste-Hilfe-Kurse für Betriebe (DGUV)
                  </h1>
                  
                  <p className="text-white/90 text-lg mt-3 drop-shadow-sm">
                    Ersthelferinnen und Ersthelfer für Ihren Betrieb – praxisnah und bundesweit anerkannt.
                  </p>
                  
                  {/* Zusätzlicher Teaser-Text */}
                  <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                    <p className="text-white font-semibold text-lg">
                      Sie möchten Ersthelfer in Ihrem Betrieb ausbilden lassen?
                    </p>
                    <p className="text-white/80 text-sm mt-1">
                      Wir übernehmen die komplette Abrechnung mit Ihrer Berufsgenossenschaft – für Sie komplett kostenfrei.
                    </p>
                  </div>
                </div>
                
                {/* CTA Button - Rechts, vertikal zentriert */}
                <div className="flex-shrink-0 flex items-center">
                  <Button 
                    size="lg"
                    className="bg-white text-[#1e3a5f] hover:bg-slate-100 font-semibold px-8 py-6 text-base shadow-xl"
                    onClick={() => window.location.href = '/termine?bg=true'}
                  >
                    <Building2 className="w-5 h-5 mr-2" />
                    Jetzt Termin buchen
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 py-8 sm:py-12">
        <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          {/* Einleitung */}
          <Card className="border-0 shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#1e3a5f]/10 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#1e3a5f]" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-[#1e3a5f] mb-2">
                    Sie möchten Ersthelfer in Ihrem Betrieb ausbilden lassen?
                  </h2>
                  <p className="text-slate-600">
                    Unsere Kurse sind <strong>praxisnah</strong>, <strong>verständlich</strong> und <strong>bundesweit anerkannt</strong> nach DGUV-Vorgaben. 
                    Die Kosten können in der Regel über Ihre zuständige Berufsgenossenschaft abgerechnet werden.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Kostenübernahme / Abrechnung */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
                <FileText className="w-5 h-5" />
                Kostenübernahme & Abrechnung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-600">
                Für die Abrechnung über die Berufsgenossenschaft benötigen Teilnehmende ein <strong>korrekt ausgefülltes Formular</strong>.
              </p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-2">Bitte beachten:</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Der <strong>obere Bereich</strong> (Angaben des Betriebs / BG-Mitglieds) muss <strong>vollständig ausgefüllt</strong> sein</li>
                      <li><strong>Firmenstempel</strong> und <strong>Unterschrift der Firma</strong> sind erforderlich</li>
                      <li>Den <strong>unteren Bereich</strong> (Angaben der Ausbildungsstelle) füllen wir vor Ort aus</li>
                      <li>Ein Formular kann (je nach BG) bis zu <strong>10 Teilnehmende</strong> abdecken</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Ausfüllhilfe Download */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-blue-800 mb-2">
                      <strong>Ausfüllhilfe:</strong> Als Unterstützung stellen wir Ihnen eine ausgefüllte Beispiel-Ausfüllhilfe zur Verfügung. 
                      Hier sehen Sie, wie das DGUV-Abrechnungsformular korrekt ausgefüllt wird.
                    </p>
                    <a
                      href="https://customer-assets.emergentagent.com/job_erste-hilfe-app-1/artifacts/wrfxu3fn_DGUV-Abrechnungsformular-Ausfu%CC%88llhilfe.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-900 font-medium text-sm underline"
                      data-testid="download-ausfuellhilfe"
                    >
                      <Download className="w-4 h-4" />
                      Beispiel-Ausfüllhilfe als PDF herunterladen
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Download-Button für DGUV-Formular */}
          <Card className="border-2 border-[#1e3a5f] shadow-lg overflow-hidden">
            <CardContent className="pt-6 pb-6 px-3 sm:px-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-[#1e3a5f] rounded-full flex items-center justify-center mx-auto">
                  <Download className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-[#1e3a5f]">Standard DGUV-Abrechnungsformular</h3>
                
                <div className="w-full max-w-md mx-auto">
                  <a
                    href="https://customer-assets.emergentagent.com/job_first-aid-bottrop/artifacts/rwzlq64c_abrechnungsformular.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full"
                    data-testid="download-dguv-form"
                  >
                    <Button className="w-full bg-red-600 hover:bg-red-700 text-white px-3 sm:px-6 py-4 text-sm sm:text-base h-auto flex items-center justify-center gap-2">
                      <Download className="w-5 h-5 flex-shrink-0" />
                      <span className="text-center">DGUV-Formular herunterladen (PDF)</span>
                    </Button>
                  </a>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4 text-left mt-4">
                  <p className="text-sm text-slate-600 mb-2">
                    <strong>Dieses Standardformular kann u. a. verwendet werden für:</strong>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {standardBGs.map((bg) => (
                      <span key={bg} className="px-3 py-1 bg-white border border-slate-200 rounded-full text-sm text-slate-700">
                        {bg}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    (ggf. weitere gemäß DGUV-Übersicht)
                  </p>
                </div>
                
                <a
                  href="https://www.dguv.de/fb-erstehilfe/mitglied/kostenuebernahme/index.jsp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-[#1e3a5f] hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  Zur offiziellen DGUV-Übersicht
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Sonderfälle */}
          <div className="space-y-3">
            <h2 className="text-xl font-semibold text-[#1e3a5f] flex items-center gap-2">
              <Info className="w-5 h-5" />
              Sonderfälle – Spezielle Formulare
            </h2>
            
            {/* BGW */}
            <Card className={`border-0 shadow-sm transition-all ${openAccordion === 'bgw' ? 'ring-2 ring-[#1e3a5f]' : ''}`}>
              <button
                onClick={() => toggleAccordion('bgw')}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
                data-testid="accordion-bgw"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-[#1e3a5f]" />
                  <span className="font-medium text-slate-800">BGW – Gesundheitsdienst & Wohlfahrtspflege</span>
                </div>
                {openAccordion === 'bgw' ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              {openAccordion === 'bgw' && (
                <CardContent className="pt-0 pb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-slate-700 mb-3">
                      Für die <strong>BGW</strong> wird ein spezielles <strong>Online-Formular</strong> verwendet. 
                      Bitte das Formular online ausfüllen/erstellen, ausdrucken und zum Kurstag mitbringen 
                      <strong> (mit Stempel & Unterschrift)</strong>.
                    </p>
                    <a
                      href="https://www.bgw-online.de/bgw-online-de/themen/sicher-mit-system/erste-hilfe/erste-hilfe-21598"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="link-bgw"
                    >
                      <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        BGW Online-Formular öffnen
                      </Button>
                    </a>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* BGN */}
            <Card className={`border-0 shadow-sm transition-all ${openAccordion === 'bgn' ? 'ring-2 ring-[#1e3a5f]' : ''}`}>
              <button
                onClick={() => toggleAccordion('bgn')}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
                data-testid="accordion-bgn"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-[#1e3a5f]" />
                  <span className="font-medium text-slate-800">BGN – Nahrungsmittel & Gastgewerbe</span>
                </div>
                {openAccordion === 'bgn' ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              {openAccordion === 'bgn' && (
                <CardContent className="pt-0 pb-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-slate-700 mb-3">
                      Bei der <strong>BGN</strong> muss das Abrechnungsformular über die BGN angefordert werden 
                      (wird dem Betrieb zugesandt). Bitte <strong>rechtzeitig vor dem Kurstermin</strong> anfordern. 
                      Ein Formular reicht in der Regel für bis zu <strong>10 Teilnehmende</strong>.
                    </p>
                    <a
                      href="https://www.bgn.de/nachricht-an-die-bgn-team-erste-hilfe"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="link-bgn"
                    >
                      <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-100">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        BGN-Formular anfordern
                      </Button>
                    </a>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Unfallkassen */}
            <Card className={`border-0 shadow-sm transition-all ${openAccordion === 'unfallkassen' ? 'ring-2 ring-[#1e3a5f]' : ''}`}>
              <button
                onClick={() => toggleAccordion('unfallkassen')}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
                data-testid="accordion-unfallkassen"
              >
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-[#1e3a5f]" />
                  <span className="font-medium text-slate-800">Unfallkassen / Öffentlicher Dienst</span>
                </div>
                {openAccordion === 'unfallkassen' ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              {openAccordion === 'unfallkassen' && (
                <CardContent className="pt-0 pb-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-slate-700 mb-3">
                      Bei Einrichtungen der <strong>öffentlichen Hand</strong> (z. B. Schulen, Kitas, Kommunen) werden 
                      häufig Gutscheine/Unterlagen der zuständigen <strong>Unfallkasse</strong> verwendet. 
                      Bitte prüfen Sie die zuständige Stelle über die DGUV-Übersicht bzw. Ihre Unfallkasse.
                    </p>
                    <a
                      href="https://www.dguv.de/fb-erstehilfe/mitglied/kostenuebernahme/index.jsp"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="link-unfallkassen"
                    >
                      <Button variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-100">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        DGUV-Übersicht öffnen
                      </Button>
                    </a>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>

          {/* Checkliste */}
          <Card className="border-0 shadow-md bg-gradient-to-br from-[#1e3a5f] to-[#2d4a6f] text-white">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-white">
                <ClipboardList className="w-5 h-5" />
                Checkliste für den Kurstag
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {[
                  'Formular herunterladen/erstellen (je nach BG)',
                  'Oberen Bereich vollständig ausfüllen',
                  'Firmenstempel + Unterschrift',
                  'Zum Kurstag mitbringen',
                  'Rest wird vor Ort ergänzt'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-white/90">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* CTA zur Terminbuchung */}
          <Card className="border-0 shadow-md">
            <CardContent className="py-8 text-center">
              <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">
                Bereit für den nächsten Schritt?
              </h3>
              <p className="text-slate-600 mb-4">
                Buchen Sie jetzt Ihren Erste-Hilfe-Kurs und bringen Sie das ausgefüllte Formular mit.
              </p>
              <a href="/termine?bg=true" data-testid="cta-termine">
                <Button className="bg-green-600 hover:bg-green-700 text-white px-8 py-3">
                  Termin buchen (BG-Kostenübernahme)
                </Button>
              </a>
            </CardContent>
          </Card>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BetrieblicheErsteHilfePage;
